import Cookie from 'cookie-cutter';
import { contentSelect, streamSelect } from 'common/actions/appActions';
import { getEvent, getVideo } from 'common/actions/apiAction';
import { userLocationSelector } from 'common/selectors/authenticationSelectors';
import fanAutoStartSelector from 'common/selectors/fanAutoStartSelector';
import routingSelector from 'common/selectors/routingSelector';
import pageSelector from 'common/selectors/pageSelector';
import selectedContentSelector from 'common/selectors/selectedContentSelector';
import { nielsenTrackingSelector } from 'common/selectors/analyticsSelectors';
import { roadblockSelector } from 'common/selectors/appSelector';
import { PlayerPlacement } from 'enums';
import { locationBlackedOutForContent } from 'lib/auth';

export const AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR';
export const AUTOPLAY_UPDATED = 'AUTOPLAY_UPDATED';
export const AUTOEMBED_TIMER_COMPLETE = 'AUTOEMBED_TIMER_COMPLETE';
export const CLEAR_PLAYING_STREAM = 'CLEAR_PLAYING_STREAM';
export const STREAM_PLAYING = 'STREAM_PLAYING';
export const PLAYER_EMBEDDING = 'PLAYER_EMBEDDING';
export const PLAYER_EMBEDDED = 'PLAYER_EMBEDDED';
export const PLAYER_INITIALIZED = 'PLAYER_INITIALIZED';
export const PLAYER_READY = 'PLAYER_READY';
export const PROGRAM_CHANGE = 'PROGRAM_CHANGE';
export const RECEIVER_AVAILABLE = 'RECEIVER_AVAILABLE';
export const REGISTER_PLAYER = 'REGISTER_PLAYER';
export const RESET_PLAYER = 'RESET_PLAYER';
export const FEED_SELECT = 'FEED_SELECT';
export const FEED_RESET = 'FEED_RESET';

export function createRegisterPlayerAction() {
    return {
        type: REGISTER_PLAYER,
        ready: false
    };
}

export function createPlayerEmbeddingAction(embedding = false) {
    return {
        type: PLAYER_EMBEDDING,
        embedding
    };
}

export function createPlayerEmbeddedAction(embedded = false) {
    return {
        type: PLAYER_EMBEDDED,
        embedded
    };
}

export function createPlayerInitializedAction(initialized = false) {
    return {
        type: PLAYER_INITIALIZED,
        initialized
    };
}


export function createResetPlayerAction() {
    return {
        type: RESET_PLAYER
    };
}

export function createAutoEmbedTimerCompleteAction() {
    return {
        type: AUTOEMBED_TIMER_COMPLETE
    };
}

export function createAutoplayUpdatedAction(autoplay = true) {
    return {
        type: AUTOPLAY_UPDATED,
        autoplay
    };
}

export function createStreamPlayingAction({ stream, error = false }) {
    return {
        type: STREAM_PLAYING,
        stream,
        error
    };
}

export function createClearPlayingStreamAction() {
    return {
        type: CLEAR_PLAYING_STREAM
    };
}

export function createFeedSelectAction(streamId) {
    return (dispatch, getState) => {
        const content = selectedContentSelector(getState());
        dispatch({
            type: FEED_SELECT,
            content,
            streamId
        });
    };
}

export function createFeedResetAction() {
    return {
        type: FEED_RESET
    };
}

export function createPlayerReadyAction(playerPlacement, content = {}) {
    return (dispatch, getState) => {
        const state = getState();
        const { app: { isMobile = false } } = state;
        const { id: pageId } = pageSelector(state);
        const { locationBeforeTransitions: { query: { id, autoStart } = {} } = {} } = routingSelector(state);
        const roadblock = roadblockSelector(state);
        const fanAutoStart = fanAutoStartSelector(state);

        let autoEmbed = true;
        // always autoEmbed if an id is passed, or user is on player page
        if (!id && pageId !== 'player') {
            const videoAutoStartCookie = Cookie.get('VAS');
            if (fanAutoStart !== null) {
                autoEmbed = (fanAutoStart === '1' || fanAutoStart === 1);
            } else if (videoAutoStartCookie) {
                // check for cookie autoplay (1=on, anything else=off)
                autoEmbed = (videoAutoStartCookie === '1' || videoAutoStartCookie === 1);
            }
        }

        // don't autoembed users who are blacked out for content
        if (playerPlacement === PlayerPlacement.HERO) {
            const userLocation = userLocationSelector(state);
            autoEmbed = !locationBlackedOutForContent(userLocation, content);
        }

        // don't autoEmbed if it's authenticated content on mobile (only respect this if roadblock is enabled)
        if (roadblock) {
            const { authTypes = [] } = content;
            if (isMobile && authTypes.length > 0) {
                autoEmbed = false;
            }
        }

        if (autoStart === 'false') {
            autoEmbed = false;
        }

        const action = {
            type: PLAYER_READY,
            autoEmbed,
            ready: true
        };

        dispatch(action);
        return action;
    };
}

export function createReceiverAvailableAction() {
    return {
        type: RECEIVER_AVAILABLE
    };
}

export function createAuthorizationErrorAction(message) {
    return {
        type: AUTHORIZATION_ERROR,
        message
    };
}

// TODO: We should be able to check if content is already available in bucket and not get from network
export function createProgramChangeAction(query, streamId) {
    return async (dispatch, getState) => {
        dispatch({ type: PROGRAM_CHANGE, query });
        const resp = await dispatch(getEvent({ query }));
        let trackingData;
        if (resp && resp.page && resp.page.contents) {
            // preserve custom "placement" property by merging the next content rather than replacing
            const selectedContent = selectedContentSelector(getState());
            const content = { ...selectedContent, ...resp.page.contents };
            content.reEmbed = false;  // don't initiate reEmbed for program changes or playlists
            const stream = (content.streams && content.streams.filter(({ id }) => id === streamId)[0]) || content.streams[0];
            dispatch(contentSelect(content));
            await dispatch(streamSelect(stream));
            dispatch(createStreamPlayingAction({ stream }));
            trackingData = nielsenTrackingSelector(getState());
            return trackingData;
        }

        return trackingData;
    };
}

// TODO: We should be able to check if content is already available in bucket and not get from network
export function createContinuousPlayAction(streamId) {
    return (dispatch, getState) => dispatch(getVideo({ pathParams: streamId })).then((resp) => {
        if (resp && resp.page && resp.page.contents) {
            // preserve custom "placement" property by merging the next content rather than replacing
            const selectedContent = selectedContentSelector(getState());
            const content = { ...selectedContent, ...resp.page.contents };
            content.reEmbed = false;  // don't initiate reEmbed for program changes or playlists
            const stream = (content.streams && content.streams.filter(({ id }) => id === streamId)[0]) || content.streams[0];
            dispatch(contentSelect(content));
            dispatch(streamSelect(stream));
            dispatch(createStreamPlayingAction({ stream }));
        }
    });
}



// WEBPACK FOOTER //
// ./app/webapp/player/actions/index.js